import React, {
	useContext,
	forwardRef,
	useState,
	useRef,
	useCallback,
	ReactNode,
	FC,
	HTMLAttributes,
	Children,
	isValidElement,
} from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useWindowSize } from 'react-use';
import { NavHashLink } from 'react-router-hash-link';
import { Manager, Popper, Reference } from 'react-popper';
// @ts-ignore
import useEventOutside from '@omtanke/react-use-event-outside';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/icon/Icon';
import ThemeContext from '../../contexts/themeContext';
import Collapse from '../../components/bootstrap/Collapse';
import useDarkMode from '../../hooks/useDarkMode';
import { TIcons } from '../../type/icons-type';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { isFullyRestricted } from '../../helpers/menuVisibility';

interface IListProps extends HTMLAttributes<HTMLUListElement> {
	id?: string;
	children?: ReactNode;
	className?: string;
	ariaLabelledby?: string;
	parentId?: string;
	rootId?: string;
	horizontal?: boolean;
	isBeta?: boolean;
}
export const List = forwardRef<HTMLUListElement, IListProps>(
	({ id, children, className, ariaLabelledby, parentId, rootId, horizontal, ...props }, ref) => {
		return (
			<ul
				ref={ref}
				id={id}
				className={classNames('navigation', { 'navigation-menu': horizontal }, className)}
				aria-labelledby={ariaLabelledby}
				data-bs-parent={
					parentId === `${rootId}__${rootId}`
						? `#${rootId}`
						: (parentId && `#${parentId}`) || null
				}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...props}>
				{children}
			</ul>
		);
	},
);
List.displayName = 'List';
List.propTypes = {
	id: PropTypes.string,
	children: PropTypes.node,
	className: PropTypes.string,
	ariaLabelledby: PropTypes.string,
	parentId: PropTypes.string,
	rootId: PropTypes.string,
	horizontal: PropTypes.bool,
};
List.defaultProps = {
	id: undefined,
	children: null,
	className: undefined,
	ariaLabelledby: undefined,
	parentId: undefined,
	rootId: undefined,
	horizontal: false,
};

interface IItemProps {
	children?: ReactNode;
	to?: string;
	title?: string;
	icon?: TIcons;
	id?: string;
	parentId?: string;
	rootId: string;
	isHorizontal?: boolean;
	notification?: boolean | string;
	isMore?: boolean;
	hide?: boolean;
	isDisable?: boolean;
	lockedActionIcon?: TIcons;
	lockedActionTitle?: string;
	onLockedAction?: () => void;
	lockedActionPulse?: boolean;
	iconBlink?: boolean;
	activeItem?: string;
	setActiveItem?(...args: unknown[]): unknown;
	isBeta?: boolean;
}
export const Item: FC<IItemProps> = ({
	children,
	to,
	title,
	icon,
	id,
	parentId,
	rootId,
	isHorizontal,
	notification,
	isMore,
	hide,
	isDisable,
	lockedActionIcon,
	lockedActionTitle,
	onLockedAction,
	lockedActionPulse,
	iconBlink,
	isBeta,
	...props
}) => {
	const dispatch = useDispatch();
	const { darkModeStatus } = useDarkMode();
	const { width } = useWindowSize();
	const { setAsideStatus, setLeftMenuStatus, setRightMenuStatus } = useContext(ThemeContext);

	// eslint-disable-next-line react/prop-types
	const ACTIVE = props.activeItem === id;

	const handleClick = () => {
		if (typeof props.setActiveItem !== 'undefined') {
			// eslint-disable-next-line react/prop-types, @typescript-eslint/no-unused-expressions
			ACTIVE ? props.setActiveItem(null) : props.setActiveItem(id);
		}
	};

	const linkHandleClick = () => {
		// For Mobile Design
		if (width < Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE)) setAsideStatus(false);
		setLeftMenuStatus(false);
		setRightMenuStatus(false);
		dispatch.filters.cleanFilters();
	};

	const handleLinkClick = (e: React.MouseEvent) => {
		if (isDisable) {
			e.preventDefault();
			e.stopPropagation();
			return;
		}
		linkHandleClick();
	};

	const ANCHOR_LINK_PATTERN = /^#/i;
	const location = useLocation();
	const normalizePath = (p: string) => p.replace(/^\/+|\/+$/g, '');
	const pathnameNorm = normalizePath(location.pathname);
	const isPathActive = (p?: string) => {
		if (typeof p !== 'string' || !p) return false;
		const n = normalizePath(p);
		return pathnameNorm === n || pathnameNorm.startsWith(`${n}/`);
	};

	// Paths belonging to this item's OWN rendered subMenu (if any). A parent
	// item (e.g. "Setup / Admin") should only highlight for its real
	// children — not for a sibling item elsewhere in the menu whose path
	// happens to share the same leading segment.
	const ownSubMenuPaths = children
		? Children.toArray(children)
			.map((child) => (isValidElement(child) ? (child.props as { to?: string }).to : undefined))
			.filter((p): p is string => typeof p === 'string')
		: [];

	// For aside menu
	const here =
		typeof to === 'string' &&
		to !== '/' &&
		(ownSubMenuPaths.length > 0
			? pathnameNorm === normalizePath(to) || ownSubMenuPaths.some(isPathActive)
			: isPathActive(to));
	// For top menu
	const match = to !== '/' && location.pathname === to;

	const { t } = useTranslation('menu');

	const LINK_CLASS = classNames('navigation-link', 'navigation-link-pill', {
		collapsed: !!children && !isHorizontal,
		active: isHorizontal ? match : here,
		'navigation-link-disabled': isDisable,
	});

	const INNER = (
		<>
			<span className='navigation-link-info'>
				{icon && (
					<Icon
						className={classNames('navigation-icon', { 'navigation-icon-blink': iconBlink })}
						icon={icon}
					/>
				)}
				{/* {title && <span className='navigation-text'>{t(title)}</span>} */}
				{title && (
					<span className='navigation-text'>
						{t(title)}
						{isBeta && <span className='navigation-beta-badge'>BETA</span>}
					</span>
				)}
			</span>
			{(!!children || !!notification) && (
				<span className='navigation-link-extra'>
					{!!notification && (
						<Icon
							icon='Circle'
							className={classNames(
								'navigation-notification',
								{
									[`text-${notification}`]: typeof notification === 'string',
									'text-danger': typeof notification !== 'string',
								},
								'animate__animated animate__heartBeat animate__infinite animate__slower',
							)}
						/>
					)}
					{!!children && <Icon className='navigation-arrow' icon='ChevronRight' />}
				</span>
			)}
		</>
	);

	const WITHOUT_CHILD =
		!children &&
		!hide &&
		((typeof to === 'string' && ANCHOR_LINK_PATTERN.test(to) && (
			<NavHashLink
				className={LINK_CLASS}
				to={to}
				onClick={handleLinkClick}
				aria-disabled={isDisable}
				tabIndex={isDisable ? -1 : undefined}>
				{INNER}
			</NavHashLink>
		)) || (
				<NavLink
					// @ts-ignore
					className={classNames(LINK_CLASS, ({ isActive }) => (isActive ? 'active' : ''))}
					to={`../${to}`}
					onClick={linkHandleClick}>
					{INNER}
				</NavLink>
			));

	// Small standalone action button shown alongside a disabled item — e.g.
	// a "Renew" icon on a locked "Fleet Sage" nav entry, since the item
	// itself no longer navigates anywhere while disabled. Stops propagation
	// so it never triggers the (already-blocked) link click underneath it.
	const LOCKED_ACTION =
		isDisable && onLockedAction ? (
			<button
				type='button'
				className={classNames('navigation-locked-action', {
					'navigation-locked-action--pulse': lockedActionPulse,
				})}
				title={lockedActionTitle}
				aria-label={lockedActionTitle || 'Renew'}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					onLockedAction();
				}}>
				<Icon icon={lockedActionIcon || 'Autorenew'} className='navigation-locked-action-icon' />
			</button>
		) : null;

	// Dropdown
	const dropdownRef = useRef(null);

	const dropdownButtonRef = useRef(null);
	const setButtonRef = useCallback((node: null, ref: (arg0: any) => any) => {
		dropdownButtonRef.current = node;
		return ref(node);
	}, []);

	const dropdownListRef = useRef(null);
	const setListRef = useCallback((node: null, ref: (arg0: any) => any) => {
		dropdownListRef.current = node;
		return ref(node);
	}, []);

	const [dropdownStatus, setDropdownStatus] = useState(false);

	const dropdownButtonHandleClick = () => {
		setDropdownStatus(!dropdownStatus);
	};

	// Clicking outside to close
	const closeMenu = useCallback(() => {
		setDropdownStatus(false);
	}, []);
	useEventOutside(dropdownRef, 'mousedown', closeMenu);
	useEventOutside(dropdownRef, 'touchstart', closeMenu);

	if (children) {
		// Parent items with a subMenu (e.g. "Setup / Admin") skipped the hide
		// check entirely — only leaf items (WITHOUT_CHILD, below) respected it.
		// That's why a permission-gated parent tab stayed visible even when
		// hideNavItem/isFullyRestricted correctly returned true for it.
		if (hide) return null;
		// submenu && in header
		if (isHorizontal) {
			return (
				<Manager>
					<li
						ref={dropdownRef}
						className={classNames('navigation-item', 'dropdown', {
							'navigation-item-more': isMore,
						})}>
						<Reference>
							{({ ref }) => (
								<span
									// @ts-ignore
									ref={(node) => setButtonRef(node, ref)}
									id={`${rootId}__${id}--link`}
									className={LINK_CLASS}
									// data-bs-toggle='dropdown'
									// data-bs-target={`#${rootId}__${id}`}
									aria-expanded={dropdownStatus}
									aria-controls={`${rootId}__${id}`}
									role='button'
									tabIndex={-1}
									onClick={dropdownButtonHandleClick}
									onKeyDown={dropdownButtonHandleClick}>
									{INNER}
								</span>
							)}
						</Reference>
						{dropdownStatus && (
							<Popper
								placement='bottom-start'
								modifiers={[
									{
										name: 'flip',
										options: {
											fallbackPlacements: [`bottom-end`, `bottom-start`],
										},
									},
								]}>
								{({ ref, style, placement }) => (
									<List
										// @ts-ignore
										ref={(node) => setListRef(node, ref)}
										style={style}
										data-placement={placement}
										id={`${rootId}__${id}`}
										className={classNames(
											'dropdown-menu',
											{
												'dropdown-menu-dark': darkModeStatus,
											},
											'show',
										)}
										ariaLabelledby={`${rootId}__${id}--link`}
										rootId={rootId}
										parentId={`${rootId}__${parentId}`}
										onMouseLeave={() => setDropdownStatus(false)}>
										{children}
									</List>
								)}
							</Popper>
						)}
					</li>
				</Manager>
			);
		}
		// submenu && in aside
		return (
			<li className='navigation-item'>
				<span
					id={`${rootId}__${id}--link`}
					className={LINK_CLASS}
					aria-expanded={ACTIVE}
					aria-controls={`${rootId}__${id}`}
					role='button'
					tabIndex={-1}
					onClick={handleClick}
					onKeyDown={handleClick}>
					{INNER}
				</span>
				<Collapse isOpen={ACTIVE} isChildClone>
					<List
						id={`${rootId}__${id}`}
						ariaLabelledby={`${rootId}__${id}--link`}
						rootId={rootId}
						parentId={`${rootId}__${parentId}`}
						onMouseLeave={closeMenu}>
						{children}
					</List>
				</Collapse>
			</li>
		);
	}
	// without submenu
	return (
		<li className={classNames('navigation-item', { 'navigation-item-with-action': !!LOCKED_ACTION })}>
			{WITHOUT_CHILD}
			{LOCKED_ACTION}
		</li>
	);
};
Item.propTypes = {
	children: PropTypes.node,
	to: PropTypes.string,
	title: PropTypes.string,
	icon: PropTypes.string,
	id: PropTypes.string,
	parentId: PropTypes.string,
	rootId: PropTypes.string.isRequired,
	isHorizontal: PropTypes.bool,
	notification: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	isMore: PropTypes.bool,
	hide: PropTypes.bool,
	isDisable: PropTypes.bool,
	lockedActionIcon: PropTypes.string,
	lockedActionTitle: PropTypes.string,
	onLockedAction: PropTypes.func,
	lockedActionPulse: PropTypes.bool,
	iconBlink: PropTypes.bool,
	isBeta: PropTypes.bool,
};
Item.defaultProps = {
	children: null,
	to: undefined,
	title: undefined,
	icon: undefined,
	id: undefined,
	parentId: undefined,
	isHorizontal: false,
	notification: false,
	isMore: false,
	hide: false,
	isDisable: false,
	lockedActionIcon: undefined,
	lockedActionTitle: undefined,
	onLockedAction: undefined,
	lockedActionPulse: false,
	iconBlink: false,
	isBeta: false,
};

interface INavigationLineProps {
	className?: string;
}
export const NavigationLine: FC<INavigationLineProps> = ({ className }) => {
	return <hr className={classNames('navigation-line', className)} />;
};
NavigationLine.propTypes = {
	className: PropTypes.string,
};
NavigationLine.defaultProps = {
	className: undefined,
};

interface INavigationTitleProps extends HTMLAttributes<HTMLSpanElement> {
	className?: string;
	children: ReactNode;
}
export const NavigationTitle: FC<INavigationTitleProps> = ({ className, children, ...props }) => {
	return (
		<li className='navigation-item'>
			{/* eslint-disable-next-line react/jsx-props-no-spreading */}
			<span className={classNames('navigation-title', className)} {...props}>
				{children}
			</span>
		</li>
	);
};
NavigationTitle.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
NavigationTitle.defaultProps = {
	className: undefined,
};

interface INavigationProps {
	horizontal?: boolean;
	menu: {
		[key: string]: {
			id?: string | number;
			text?: string;
			path?: string;
			icon?: TIcons;
			isDisable?: boolean;
			lockedActionIcon?: TIcons;
			lockedActionTitle?: string;
			onLockedAction?: () => void;
			lockedActionPulse?: boolean;
			iconBlink?: boolean;
			isBeta?: boolean;
			subMenu?: {
				[key: string]: {
					id?: string | number;
					text?: string;
					path?: string;
					icon?: TIcons;
					isDisable?: boolean;
				};
			} | null;
		};
	};
	id: string;
	className?: string;
}
const Navigation = forwardRef<HTMLElement, INavigationProps>(
	({ menu, horizontal, id, className, ...props }, ref) => {
		const [activeItem, setActiveItem] = useState(undefined);

		const { t } = useTranslation('menu');

		const permissions: any = useSelector((state: RootState) => state.auth?.permissions);

		const hideNavItem = (item: string, hide: boolean) => {
			if (permissions && (permissions[item] === undefined || permissions[item])) {
				if (!hide) {
					return false;
				} else {
					return true;
				}
			} else {
				return true;
			}
		};

		function fillMenu(
			data:
				| {
					id?: string | number;
					text?: string;
					path?: string;
					icon?: TIcons;
					isDisable?: boolean;
					lockedActionIcon?: TIcons;
					lockedActionTitle?: string;
					onLockedAction?: () => void;
					lockedActionPulse?: boolean;
					iconBlink?: boolean;
					isBeta?: boolean;
					subMenu?:
					| {
						id?: string | number;
						text?: string;
						path?: string;
						icon?: TIcons;
						isDisable?: boolean;
					}[]
					| undefined;
				}[]
				| any,
			parentId: string,
			rootId: string,
			isHorizontal: boolean | undefined,
			isMore: boolean | undefined,
		) {
			return Object.keys(data).map((item) =>
				data[item].path ? (
					<Item
						key={data[item].id}
						rootId={rootId}
						id={data[item].id}
						title={data[item].text}
						icon={data[item].icon}
						to={`${data[item].path}`}
						parentId={parentId}
						isHorizontal={isHorizontal}
						setActiveItem={setActiveItem}
						activeItem={activeItem}
						notification={data[item].notification}
						// hide={hideNavItem(data[item].id, data[item].hide)}
						hide={
							hideNavItem(data[item].id, data[item].hide) ||
							isFullyRestricted(permissions, data[item].permissionKeys)
						}
						isDisable={data[item].isDisable}
						lockedActionIcon={data[item].lockedActionIcon}
						lockedActionTitle={data[item].lockedActionTitle}
						onLockedAction={data[item].onLockedAction}
						lockedActionPulse={data[item].lockedActionPulse}
						iconBlink={data[item].iconBlink}
						isBeta={data[item].isBeta}>
						{!!data[item].subMenu &&
							fillMenu(
								data[item].subMenu,
								data[item].id,
								rootId,
								isHorizontal,
								undefined,
							)}
					</Item>
				) : (
					!isMore &&
					!isHorizontal && (
						<NavigationTitle key={data[item].id}>{t(data[item].text)}</NavigationTitle>
					)
				),
			);
		}

		return (
			// @ts-ignore
			// eslint-disable-next-line react/jsx-props-no-spreading
			<nav ref={ref} aria-label={id} className={className} {...props}>
				<List id={id} horizontal={horizontal}>
					{fillMenu(menu, id, id, horizontal, undefined)}
					{horizontal && (
						<Item
							rootId={`other-${id}`}
							title={t('More')}
							icon='MoreHoriz'
							isHorizontal
							isMore>
							{fillMenu(menu, `other-${id}`, `other-${id}`, false, true)}
						</Item>
					)}
				</List>
			</nav>
		);
	},
);
Navigation.displayName = 'Navigation';
Navigation.propTypes = {
	horizontal: PropTypes.bool,
	// @ts-ignore
	menu: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		text: PropTypes.string,
		path: PropTypes.string,
		icon: PropTypes.string,
		isDisable: PropTypes.bool,
		subMenu: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				text: PropTypes.string,
				path: PropTypes.string,
				icon: PropTypes.string,
				isDisable: PropTypes.bool,
			}),
		),
	}).isRequired,
	id: PropTypes.string.isRequired,
	className: PropTypes.string,
};
Navigation.defaultProps = {
	horizontal: false,
	className: undefined,
};

export default Navigation;